import type { QueryClient } from '@tanstack/react-query';
import { redirect, type RouteObject } from 'react-router-dom';

import { getGamesQueryOptions } from '@/api/getGames';
import { getMyPermissionsQueryOptions } from '@/api/getMyPermissions';
import { getSandboxesQueryOptions } from '@/api/getSandboxes';
import { getTranslationLanguagesQuery } from '@/api/translation/getTranslationLanguages';
import {
  firstAllowed,
  holdsPermissionsInLoader,
  outOfScope,
  requirePermissions,
} from '@/app/requirePermissions';
import { SandboxPublishButton } from '@/app/sandboxPublish/sandboxPublishButton';
import type { CrumbHandle } from '@/components/breadcrumb/breadcrumb';
import {
  GAME_PAGES,
  SANDBOX_PAGES,
  sandboxPageGates,
} from '@/components/sidebar/pages';
import { GameBreadcrumbDropdown } from '@/features/game/components/gameBreadcrumbDropdown';
import { SandboxBreadcrumbDropdown } from '@/features/sandbox/components/sandboxBreadcrumbDropdown';
import { ensureAppQueryData } from '@/hooks/useAppQuery';
import { setGameScope, setSandboxScope } from '@/lib/api-client';
import { savedSandbox, storeGameSandbox } from '@/lib/utils';
import type { Game } from '@/models/game';
import { PagePermissions } from '@/models/pagePermissions';
import { Permissions } from '@/models/permission';
import type { PlayerModel } from '@/models/players';

import ErrorPage from '../error-page';
import Dashboard from './dashboard';
import { GameLayout } from './GameLayout';
import { giftRoute } from './gift/route';
import { itemRoute } from './items/route';
import { sandboxImportRoute } from './sandboxImport/route';
import { seasonPassRoute } from './seasonPass/route';
import { settingsRoute } from './settings/route';
import { storeRoute } from './store/route';
import { transactionsRoute } from './transactions/route';
import { translationsRoute } from './translations/route';

const MainDashboard = <Dashboard menu items={SANDBOX_PAGES} />;
const GameDashboard = <Dashboard menu items={GAME_PAGES} />;
export const gameRoute = (queryClient: QueryClient): RouteObject => ({
  path: ':gameCode',
  id: 'game',
  handle: {
    crumb: ({ data, params }) => (
      <GameBreadcrumbDropdown
        gameCode={params.gameCode ?? ''}
        gameName={data?.name ?? params.gameCode ?? ''}
      />
    ),
    to: false,
  } satisfies CrumbHandle<Game>,
  loader: ({ params }) => {
    if (params.gameCode) {
      // Owns the game half of the request scope, for this branch and every child.
      setGameScope(params.gameCode);
    }

    // Warm the viewer's own Grants: the sidebar gates every entry on them and fails
    // closed, so without this the menu paints empty on a cold cache. Non-blocking —
    // navigation shouldn't wait on it. The catch keeps a failed read from
    // becoming an unhandled rejection — the gates handle the failure themselves.
    void queryClient
      .ensureQueryData(getMyPermissionsQueryOptions())
      .catch(() => undefined);

    return queryClient.ensureQueryData(getGamesQueryOptions()).then((data) => {
      // A Game only exists under its own Studio: TDUSC typed under another
      // Studio's URL is out of scope, not a missing page.
      const game = data.games.find(
        (g) =>
          g.game_code === params.gameCode &&
          g.studio_code === params.studioCode,
      );
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- see forbidden()
      if (!game) throw outOfScope();

      return game;
    });
  },
  children: [
    {
      index: true,
      loader: async ({ params }) => {
        const { gameCode } = params;
        const saved = gameCode ? savedSandbox(gameCode) : undefined;
        if (gameCode && saved !== undefined) {
          // Validate that the saved sandbox still exists
          const { sandboxes } = await queryClient.ensureQueryData(
            getSandboxesQueryOptions({ game_code: gameCode }),
          );
          if (sandboxes.some((s) => s.code === saved)) {
            return redirect(saved);
          }
        }
        return redirect('sandboxes');
      },
    },
    {
      element: GameDashboard,
      loader: () => {
        setSandboxScope();
        return null;
      },
      children: [
        {
          errorElement: <ErrorPage />,
          children: [
            {
              path: 'sandboxes',
              lazy: () => import('./sandboxes'),
            },
          ],
        },
      ],
    },
    {
      path: ':sandboxCode',
      id: 'sandbox',
      handle: {
        crumb: () => <SandboxBreadcrumbDropdown />,
        to: false,
        action: () => <SandboxPublishButton />,
      } satisfies CrumbHandle,
      loader: ({ params }) => {
        const { gameCode, sandboxCode } = params;
        if (!gameCode || !sandboxCode) {
          throw new Error('Game code and sandbox code are required');
        }
        setSandboxScope(sandboxCode);

        storeGameSandbox(gameCode, sandboxCode);

        void holdsPermissionsInLoader(
          { oneOf: [Permissions.languages.read] },
          params,
        )
          .then((allowed) => {
            if (!allowed) return;

            void ensureAppQueryData(
              queryClient,
              { studioCode: params.studioCode ?? '', gameCode, sandboxCode },
              getTranslationLanguagesQuery(),
            );
          })
          // The Grants read this awaits can itself fail; without the catch that
          // becomes an unhandled rejection on every Sandbox navigation.
          .catch(() => undefined);

        return null;
      },
      element: <GameLayout />,
      children: [
        {
          index: true,
          loader: firstAllowed(
            sandboxPageGates,
            ({ studioCode, gameCode }) =>
              `/studios/${studioCode}/${gameCode}/sandboxes`,
          ),
        },
        {
          element: MainDashboard,
          children: [
            {
              // Same pathless boundary as the Game-level block above.
              errorElement: <ErrorPage />,
              children: [
                {
                  path: 'players',
                  loader: requirePermissions({
                    oneOf: PagePermissions.players,
                  }),
                  children: [
                    {
                      index: true,
                      lazy: async () => {
                        const { loader, Component } = await import('./players');
                        return { loader: loader(queryClient), Component };
                      },
                    },
                    {
                      path: ':playerId',
                      id: 'player',
                      handle: {
                        crumb: ({ data, params }) =>
                          data?.player.platform_user_id ?? params.playerId,
                      } satisfies CrumbHandle<{ player: PlayerModel }>,
                      lazy: async () => {
                        const { loader, Player } = await import('./player');
                        return {
                          loader: loader(queryClient),
                          Component: Player,
                        };
                      },
                    },
                  ],
                },
                { ...itemRoute(queryClient) },
                { ...seasonPassRoute(queryClient) },
                { ...giftRoute(queryClient) },
                { ...translationsRoute(queryClient) },
                { ...transactionsRoute(queryClient) },
                { ...storeRoute(queryClient) },
                { ...sandboxImportRoute },
                { ...settingsRoute },
                // Matching the leftovers keeps an unknown URL's 404 inside the
                // panel: React Router's own is raised above every boundary here.
                {
                  path: '*',
                  loader: () => {
                    // eslint-disable-next-line @typescript-eslint/only-throw-error
                    throw new Response(null, {
                      status: 404,
                      statusText: 'Not Found',
                    });
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});
